import Header from "./Header";
import Sidebar from "./Sidebar";
import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { fetchAuthSession, signOut } from "../../utils/authSession";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../css/HRPortal.css";
import { Outlet } from "react-router-dom";
import { SourcingConfigProvider } from "../../contexts/SourcingConfigContext";
import Cookies from "js-cookie";
import { LogOut } from "lucide-react";
import Loader from "../Loader";
import ImpersonationBanner from "../ImpersonationBanner";
import AnalyticsWatcher from "../analytics/AnalyticsWatcher";
import PlanStatusBar from "../billing/PlanStatusBar";

const Layout = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  // const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hr_accessToken = Cookies.get("accessToken");
  const impersonationToken = Cookies.get("impersonationToken");
  const mainRef = useRef(null);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    setIsLoading(true);
    const checkSession = async () => {
      try {
        // Validate Cognito session exists first (required for both normal and impersonation flows)
        const session = await fetchAuthSession();
        const { accessToken } = session.tokens ?? {};

        if (!accessToken) {
          throw new Error("No valid session");
        }

        // Check for impersonation (now safe - we know Cognito session is valid)
        if (impersonationToken) {
          const storedUser = localStorage.getItem("impersonatedUser");
          if (storedUser) {
            const impersonatedUser = JSON.parse(storedUser);
            if (impersonatedUser.user_type === "HR") {
              setIsAuthenticated(true);
              setIsImpersonating(true);
              setIsLoading(false);
              return;
            }
          }
        }

        const groups = accessToken.payload["cognito:groups"] ?? [];
        if (
          groups.includes("HR") &&
          hr_accessToken === accessToken.toString()
        ) {
          setIsAuthenticated(true);
        } else {
          await new Promise((resolve) => {
            Cookies.remove("accessToken");
            resolve();
          });
          await signOut();
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkSession();
  }, [location.pathname, hr_accessToken, impersonationToken]);

  const handleSignOut = async () => {
    try {
      Cookies.remove("accessToken");
      Cookies.remove("impersonationToken");
      localStorage.removeItem("impersonatedUser");
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error during sign out:", error);
    }
  };
  if (isLoading) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return (
     <div className="min-h-screen flex items-center justify-center dark:app-background p-4 grad">
             <div className="max-w-md w-full">
               <div className="relative overflow-hidden rounded-xl dark:shadow-2xl shadow-sm p-8">
                 <div className="absolute inset-0 z-0 dark:glass-effect bg-bgPrimary" />
                 <div className="relative z-10 text-center space-y-6">
                   <div className="mx-auto dark:bg-white/10 bg-bgSecondary rounded-full p-4 w-fit backdrop-blur-sm">
                     <LogOut className="w-8 h-8 dark:text-white text-textPrimary" />
                   </div>
                   <div className="space-y-2">
                     <h2 className="text-2xl font-bold dark:text-white text-textPrimary">
                       Session Expired
                     </h2>
                     <p className="dark:text-gray-300 text-textSecondary">
                       Please login again to continue your session
                     </p>
                   </div>
                   <button
                     onClick={handleSignOut}
                     className="button"
                   >
                     <span className="flex gap-2 items-center">
                       <span className="font-bold px-28">Return to Login</span>
                     </span>
                   </button>
                 </div>
               </div>
             </div>
           </div>
    );
  }

  const content = (
    // Sourcing pages under /hr are gated on the org's sourcing_enabled flag, and the header
    // hides Find Candidates without it — both read this provider, so it has to wrap the
    // portal the same way it wraps OrgAdminPortal.
    <SourcingConfigProvider>
    <div
      className="app-background grad"
      style={{
        display: "flex",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
        paddingTop: isImpersonating ? "40px" : "0",
      }}
    >
      {isImpersonating && <ImpersonationBanner />}
      {!isImpersonating && <AnalyticsWatcher />}
      <Sidebar
        // isSidebarExpanded={isSidebarExpanded}
        // setIsSidebarExpanded={setIsSidebarExpanded}
        handleSignOut={handleSignOut}
      />

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Header />
        <main
          ref={mainRef}
          className="flex-1 overflow-y-auto p-4 grad"
          style={{
            // marginLeft: isSidebarExpanded ? '0px' : '0px',
            transition: "margin-left 0.3s ease-in-out",
          }}
        >
          <PlanStatusBar role="hr" />
          <Outlet />
        </main>
      </div>
    </div>
    </SourcingConfigProvider>
  );

  return content;
};

export default Layout;
